/**
 * Class - Base class providing utility methods for all framework classes
 *
 * Extends Ancestor with comprehensive utility functions, primarily wrapping lodash
 * for framework integration. All utility methods are automatically bound to instance
 * context when used in iterators, eliminating the need for manual .bind(this).
 *
 * ARCHITECTURE: Second level in inheritance chain (Ancestor → Class).
 * Provides foundation for all framework classes by adding:
 * - Data storage (this.data, this.handlers)
 * - Collection utilities (each, map, filter, reduce)
 * - Type checking (isArray, isFunction, etc.)
 * - Object manipulation (assign, defaults, clone)
 * - Function utilities (bind, debounce, throttle)
 * - Promise utilities (when, defer, delay)
 *
 * KEY PATTERN: Iterator functions auto-bind instance context:
 * ```javascript
 * this.each(items, function(item) {
 *     this.doSomething();  // 'this' is the instance, no .bind() needed
 * });
 * ```
 *
 * @class Class
 * @augments Ancestor
 * @param {Object} [setup] - Configuration passed to instance constructor
 *
 * @example
 * // Extend Class to create custom base
 * const MyBaseClass = Class.inherit({
 *     namespace: 'MyBaseClass',
 *     prepare(setup) {
 *         this.assign(this.data, {
 *             items: []
 *         });
 *     }
 * });
 *
 * @example
 * // Using utility methods
 * const activeItems = this.filter(items, function(item) {
 *     return item.active;  // 'this' is instance context
 * });
 */
define("Class", [`Ancestor`, `module`, `lodash`, `Utils`], function (
	// ensure Config and Utils are caught by the requirejs compressor
	Super,
	Self,
	_
) {
	var dependencyCache = {};

	return Super.inherit({
		namespace: Self.id,
		requires: [`Config`, `Utils`], // additional dependencies
		prepare(setup) {
			// Initial object setup goes here
			const { Utils, namespace } = this;
			const Config = this.require(`Config`);
			const handlers = {};
			const data = {};

			Object.defineProperties(this, {
				Config: { value: Config },

				/**
				 * @method Class#assign
				 * @see Utils.assign
				 * @param {Object} destination
				 * @param {...Object} sources
				 * @returns {Object}
				 */
				assign: { value: Utils.assign },

				/**
				 * @method Class#bind
				 * @see Utils.bind
				 * @param {function} functionToBind
				 * @param {*} context
				 * @returns {function}
				 */
				bind: { value: Utils.bind },
				chain: { value: Utils.chain },

				/**
				 * @method Class#chunk
				 * @see Utils.chunk
				 * @param {Array} collection
				 * @param {number} size
				 * @returns {Array[]}
				 */
				chunk: { value: Utils.chunk },

				/**
				 * An internal cache for storing instance data, used to store serializable information.
				 * @member {Object} Class#data
				 * @example
				 * this.data.event = setup.event; // store for later use
				 */
				data: { value: data },

				/**
				 * @method Class#defaults
				 * @see Utils.defaults
				 * @param {Object} destination
				 * @param {...Object} sources
				 * @returns {Object}
				 */
				defaults: { value: Utils.defaults },

				/**
				 * @method Class#each
				 * @see Utils.each
				 */
				each: { value: Utils.each },

				/**
				 * @method Class#every
				 * @see Utils.every
				 */
				every: { value: Utils.every },

				/**
				 * @method Class#extract
				 * @see Utils.extract
				 */
				extract: { value: Utils.extract },
				extractWhere: { value: Utils.extractWhere },

				/**
				 * @method Class#find
				 * @see Utils.find
				 */
				find: { value: Utils.find },

				/**
				 * @method Class#filter
				 * @see Utils.filter
				 */
				filter: { value: Utils.filter },

				/**
				 * An internal cache for storing functions.
				 * @member {Object} Class#handlers
				 * @example
				 * this.handlers.doSomething = () => { ... };
				 */
				handlers: { value: handlers },

				/**
				 * @method Class#includes
				 * @see Utils.includes
				 */
				includes: { value: Utils.includes },

				/**
				 * @method Class#initial
				 * @see Utils.initial
				 */
				initial: { value: Utils.initial },

				/**
				 * @method Class#isArray
				 * @see Utils.isArray
				 */
				isArray: { value: Utils.isArray },

				/**
				 * @method Class#isBoolean
				 * @see Utils.isBoolean
				 */
				isBoolean: { value: Utils.isBoolean },

				/**
				 * @method Class#isDate
				 * @see Utils.isDate
				 */
				isDate: { value: Utils.isDate },

				/**
				 * @method Class#isDefined
				 * @see Utils.isDefined
				 */
				isDefined: { value: Utils.isDefined },

				/**
				 * @method Class#isElement
				 * @param {*} subject
				 * @returns {boolean}
				 * @see Utils#isElement
				 */
				isElement: { value: Utils.isElement },

				/**
				 * @method Class#isEmpty
				 * @see Utils.isEmpty
				 */
				isEmpty: { value: Utils.isEmpty },

				/**
				 * @method Class#isEqual
				 * @see Utils.isEqual
				 */
				isEqual: { value: Utils.isEqual },

				/**
				 * @method Class#isFinite
				 * @see Utils.isFinite
				 */
				isFinite: { value: Utils.isFinite },

				/**
				 * @method Class#isFunction
				 * @param {*} subject
				 * @returns {boolean}
				 * @see Utils#isFunction
				 * */
				isFunction: { value: Utils.isFunction },

				/**
				 * @method Class#isInt
				 * @see Utils.isInt
				 */
				isInt: { value: Utils.isInt },

				/**
				 * @method Class#isNull
				 * @see Utils.isNull
				 */
				isNull: { value: Utils.isNull },

				/**
				 * @method Class#isObject
				 * @see Utils.isObject
				 */
				isObject: { value: Utils.isObject },

				/**
				 * @method Class#isPromise
				 * @see Utils.isPromise
				 */
				isPromise: { value: Utils.isPromise },

				/**
				 * @method Class#isString
				 * @see Utils.isString
				 */
				isString: { value: Utils.isString },

				/**
				 * @method Class#isUndefined
				 * @see Utils.isUndefined
				 */
				isUndefined: { value: Utils.isUndefined },

				/**
				 * @method Class#keys
				 * @see Utils.keys
				 */
				keys: { value: Utils.keys },

				/**
				 * @method Class#last
				 * @see Utils.last
				 */
				last: { value: Utils.last },

				/**
				 * @method Class#map
				 * @see Utils.map
				 */
				map: { value: Utils.map },

				/**
				 * @method Class#max
				 * @see Utils.max
				 */
				max: { value: Utils.max },

				/**
				 * @method Class#min
				 * @see Utils.min
				 */
				min: { value: Utils.min },

				/**
				 * The unique, immutable namespace occupied by this Class.
				 * @member {string} Class#namespace
				 * @readonly
				 */
				namespace: { value: namespace },

				/**
				 * @method Class#parseInt
				 * @see Utils.parseInt
				 */
				parseInt: { value: Utils.parseInt },

				/**
				 * @method Class#reduce
				 * @see Utils.reduce
				 */
				reduce: { value: Utils.reduce },

				/**
				 * @method Class#strEqualsIgnoreCase
				 * @see Utils.strEqualsIgnoreCase
				 */
				strEqualsIgnoreCase: { value: Utils.strEqualsIgnoreCase },

				/**
				 * @method Class#trim
				 * @see Utils.trim
				 */
				trim: { value: Utils.trim },

				/**
				 * @method Class#unique
				 * @see Utils.unique
				 */
				unique: { value: Utils.unique },

				/**
				 * @method Class#uniqueId
				 * @see Utils.uniqueId
				 */
				uniqueId: { value: Utils.uniqueId },

				/**
				 * @method Class#where
				 * @see Utils.where
				 */
				where: { value: Utils.where },

				/**
				 * @method Class#without
				 * @see Utils.without
				 */
				without: { value: Utils.without },
			});
		},

		// constructor(setup) {
		// 	// initial construction step goes here
		// },

		// destructor(options) {
		// 	// final chance to destroy and clear everything
		// },

		api: {
			/**
			 * Create a new deferred object, for orchestrating asynchronous activities.
			 * @method Class.Deferred
			 * @returns {deferred}
			 */
			Deferred: {
				static: true,
				value() {
					const deferred = {};
					const promise = new Promise(function (resolve, reject) {
						deferred.resolve = resolve;
						deferred.reject = reject;
					});

					deferred.promise = promise;
					return deferred;
				},
			},

			/**
			 * Return a promise that will be immediately rejected with that passed value.
			 * @method Class.reject
			 * @param {*} reason
			 * @param {function} [handler]
			 * @returns {Promise}
			 */
			reject: {
				static: true,
				value(reason) {
					return Promise.reject(reason);
				},
			},

			/**
			 * @method Class#defer
			 * @param {AsyncOptions} options
			 * @returns {Promise}
			 */
			defer: {
				async: true,
				defaults: {
					duration: 0, // whether this is a defer or a full delay
				},
				value(options) {
					var deferred = this.Deferred();

					if (options.duration > 0) {
						_.delay(deferred.resolve, options.duration);
					} else {
						_.defer(deferred.resolve);
					}

					return deferred.promise;
				},
			},

			/**
			 * @method Class#defineProperties
			 * @param {Object} [target=this] - Target to define properties on
			 * @param {Object} properties - Map of property descriptors
			 * @returns {Object} The augmented target object
			 */
			defineProperties: {
				value(target, properties) {
					if (arguments.length === 1) {
						properties = target;
						target = this;
					} else if (arguments.length !== 2) {
						throw new Error(`Class.defineProperties(): Requires 1-2 arguments.`);
					}

					this.each(properties, function (property, key) {
						if (property.enumerable !== false) {
							property.enumerable = true; // only difference from the JS defaults, make visible / countable by default
						}

						if (this.isFunction(property.value)) {
							property.value = this.bind(property.value); // bind function execution to the current instance
						} else {
							if (this.isFunction(property.get)) {
								property.get = this.bind(property.get); // bind function execution to the current instance
							}
							if (this.isFunction(property.set)) {
								property.set = this.bind(property.set); // bind function execution to the current instance
							}
						}
					});

					return Object.defineProperties(target, properties);
				},
			},

			/**
			 * @method Class#defineProperty
			 * @param {Object} [target=this] - Target to define property on
			 * @param {string} key - Property name
			 * @param {Object} property - Property descriptor
			 * @returns {Object} The augmented target object
			 */
			defineProperty: {
				value(target, key, property) {
					if (arguments.length === 2) {
						property = key;
						key = target;
						target = this;
					} else if (arguments.length !== 3) {
						throw new Error(`Class.defineProperty(): Requires 2-3 arguments.`);
					}

					if (property.enumerable !== false) {
						property.enumerable = true; // only difference from the JS defaults, make visible / countable by default
					}

					if (this.isFunction(property.value)) {
						property.value = this.bind(property.value); // bind function execution to the current instance
					} else {
						if (this.isFunction(property.get)) {
							property.get = this.bind(property.get); // bind function execution to the current instance
						}
						if (this.isFunction(property.set)) {
							property.set = this.bind(property.set); // bind function execution to the current instance
						}
					}

					return Object.defineProperty(target, key, property);
				},
			},

			/**
			 * Load dependencies by namespace
			 *
			 * Loads RequireJS modules and injects them into instance context. Dependencies
			 * declared in 'requires' array are automatically loaded before constructor runs.
			 * Can also be called manually to lazily load additional dependencies.
			 *
			 * DEPENDENCY INJECTION: Loaded dependencies are injected as properties on the
			 * instance (e.g., this.XHR, this.Utils). If dependency has onDie() method,
			 * it's registered for cleanup notification when instance is destroyed.
			 *
			 * SINGLETON PATTERN: When a singleton is injected, it registers with this
			 * instance to be notified on destruction, enabling automatic cleanup.
			 *
			 * @method Class#require
			 * @param {string|Object} options - Namespace string or configuration object
			 * @param {string[]} options.deps - Array of dependency namespaces to load
			 * @param {Function} [options.then] - Success callback (receives loaded deps array)
			 * @param {Function} [options.fail] - Error callback
			 *
			 * @returns {Promise<Array>|*} Promise resolving to deps array, or cached value if string param
			 *
			 * @example
			 * // Declared dependencies (auto-loaded before constructor)
			 * requires: ['XHR', 'Utils']
			 * // Available as this.XHR, this.Utils in constructor
			 *
			 * @example
			 * // Lazy load dependencies
			 * this.require({
			 *     deps: ['User', 'ShoppingCart'],
			 *     then: function([User, ShoppingCart]) {
			 *         // Dependencies now available as this.User, this.ShoppingCart
			 *     }
			 * });
			 *
			 * @example
			 * // Get cached dependency synchronously
			 * const Utils = this.require('Utils');
			 *
			 * @see Ancestor#registerDependency
			 */
			require: {
				value(options) {
					// request an array of dependencies to load, or a reference to a previously loaded one
					if (_.isString(options)) {
						if (_.isUndefined(dependencyCache[options])) {
							return require(options);
						} else {
							return dependencyCache[options];
						}
					}

					var deferred = this.Deferred(),
						instance = this,
						promise;

					require(options.deps, function () {
						deferred.resolve(_.toArray(arguments));
					}, function (reason) {
						deferred.reject(reason);
					});

					promise = this.when({
						promise: deferred.promise, // resolves when the array of requested dependencies are ready
						then: function (deps) {
							// initial array of deps is ready
							return this.when({
								// defer again, for any dependencies that return Promises
								promises: deps,
							});
						},
					});

					promise = this.when({
						promise,
						then: function (deps) {
							// all dependencies are ready
							return _.chain(deps)
								.each(function (dep, i) {
									// iterate over all loaded dependencies
									var namespace = options.deps[i],
										api;

									if (_.isObject(dep) && _.isFunction(dep.export) && _.isUndefined(instance[dep.name])) {
										api = dep.export(instance); // get a new export api from the ingested instance

										if (_.isFunction(api.onDie)) {
											// allows notifying the ingested instance when this one diess
											if (!_.includes(instance.dependencies, dep.namespace)) {
												instance.registerDependency({
													namespace: dep.namespace,
													onDie: api.onDie,
												});
											}
											delete api.onDie; // remove from the public api
										}

										instance[dep.namespace] = api;
									}

									dependencyCache[namespace] = dep; // cache the resolved value, so this.require(namespace<String>) won't return a promise
								})
								.value();
						},
					});

					options.promise = promise;
					return this.when(options); // manual async handling required, otherwise return value type is locked to Promise
				},
			},
		},
	});
});

/**
 * A Plain Object that can contain any number of arbitrary key/value pairs.
 * @typedef {Object} Options
 * @example
 * const options = { eventId: 123 };
 * doSomething(options);
 */

/**
 * A hash that can pass asynchronous-specific options into an api call that was configured as async: true.
 * @typedef {Options} AsyncOptions
 * @property {boolean} done - If true, will end the promise chain and return undefined
 * @property {function} fail - A handler to be called with the reason upon the Promise's failure
 * @property {function} fin - A handler to be called after the Promise has resolved or failed
 * @property {function} then - A handler to be called with the Promise's result upon resolution
 */

/**
 * The object that is threaded through each step of instance construction. Affected by the Config.defaults values.
 * @typedef {Options} Setup
 * @example
 * Super.inherit({
 *   defaults: {
 *     myString: "test",
 *     myNumber: 1,
 *   },
 *   prepare(setup) {
 *     // first chance to execute code during instance construction
 *     // setup.myString and setup.myNumber are guaranteed to be defined
 *   },
 *   constructor(setup) {
 *     // second chance to execute code during instance construction
 *     // the setup object is the same one as prepare() above
 *   },
 *   ready(setup) {
 *     // final chance to execute code during instance construction
 *     // the setup object is the same one as prepare() and constructor() above
 *   },
 * });
 */

//# sourceMappingURL=Class.js.map